import React from 'react';
import { Icon, Spin } from '../components/ui.jsx';
import { api, dayN } from '../api.js';
import { MONTHS, MAT_CAT, MEZZI_CAT } from '../constants.js';

/**
 * Calendario disponibilità per asset (mezzi o attrezzature).
 * Entrambi i tab leggono dalla tabella materials, split per MEZZI_CAT.
 * Overlay cantieri SOLO per gli asset effettivamente assegnati.
 * Ciclo: libero → non disponibile (rosso) → manutenzione (arancione strisce) → libero
 */
const AssetCalendar = ({kind}) => {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth()+1);
  const [assets, setAssets] = React.useState([]);
  const [avail, setAvail] = React.useState([]);
  const [matProjMap, setMatProjMap] = React.useState([]);
  const [selA, setSelA] = React.useState('');
  const [catFilter, setCatFilter] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const isMezzi = kind === 'vehicle';
  const cfg = {
    assetEndpoint: '/materials',
    availEndpoint: '/material-availability',
    idKey: 'material_id',
    labelSingular: isMezzi ? 'mezzo' : 'attrezzatura',
    labelPlural: isMezzi ? 'mezzi' : 'attrezzature',
    renderName: a => (
      <>
        <div style={{fontWeight:600}}>{a.name}</div>
        {(a.serial_number || a.category) && (
          <div style={{fontSize:10,color:'var(--n400)'}}>
            {a.category}{a.serial_number ? ' · '+a.serial_number : ''}
          </div>
        )}
      </>
    ),
    categories: isMezzi ? MEZZI_CAT : MAT_CAT,
  };

  // Load asset list
  React.useEffect(() => {
    api(cfg.assetEndpoint)
      .then(d => {
        let items = Array.isArray(d) ? d : (d.items || []);
        if (isMezzi) items = items.filter(a => MEZZI_CAT.includes(a.category));
        else items = items.filter(a => !MEZZI_CAT.includes(a.category));
        setAssets(items);
      })
      .catch(() => setAssets([]));
  }, [kind]);

  // Load availability + asset-specific project assignments
  React.useEffect(() => {
    setLoading(true);
    const qs = selA ? `&${cfg.idKey}=${selA}` : '';
    const availUrl = `${cfg.availEndpoint}?month=${month}&year=${year}${qs}`;
    const mapUrl = `/material-project-map?month=${month}&year=${year}`;
    Promise.all([api(availUrl), api(mapUrl)])
      .then(([a, m]) => {
        setAvail(a || []);
        setMatProjMap(Array.isArray(m) ? m : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [month, year, selA, kind]);

  const prev = () => month === 1 ? (setMonth(12), setYear(y=>y-1)) : setMonth(m=>m-1);
  const next = () => month === 12 ? (setMonth(1), setYear(y=>y+1)) : setMonth(m=>m+1);

  const availMap = React.useMemo(() => {
    const m = {};
    avail.forEach(a => { const d = dayN(a.day); m[`${a[cfg.idKey]}-${d}`] = a; });
    return m;
  }, [avail]);

  // Mappa asset-specifica: material_id -> {day -> [titoli cantiere]}
  const assetProjDays = React.useMemo(() => {
    const m = {};
    matProjMap.forEach(pm => {
      const mid = pm.material_id;
      if (!m[mid]) m[mid] = {};
      const s = dayN(pm.date_start);
      const e = pm.date_end ? dayN(pm.date_end) : s;
      for (let d = s; d <= e; d++) {
        (m[mid][d] = m[mid][d] || []).push(pm.title);
      }
    });
    return m;
  }, [matProjMap]);

  const dim = new Date(year, month, 0).getDate();
  const days = Array.from({length: dim}, (_, i) => i+1);
  const dow = d => { const w = new Date(year, month-1, d).getDay(); return w === 0 ? 7 : w; };
  const isWE = d => dow(d) >= 6;

  const disp = React.useMemo(() => {
    let list = assets;
    if (selA) return assets.filter(a => a.id === +selA);
    if (catFilter) list = list.filter(a => a.category === catFilter || a.type === catFilter);
    return list;
  }, [assets, selA, catFilter]);

  const toggle = async (assetId, day) => {
    const key = `${assetId}-${day}`;
    const ex = availMap[key];
    const ds = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    const nextStatus = !ex ? 'unavailable' : ex.status === 'unavailable' ? 'maintenance' : null;

    if (nextStatus === null) {
      setAvail(p => p.filter(a => !(a[cfg.idKey] === assetId && dayN(a.day) === day)));
      await api(`${cfg.availEndpoint}/${ex.id}`, 'DELETE');
    } else if (ex) {
      setAvail(p => p.map(a => a.id === ex.id ? {...a, status: nextStatus} : a));
      try {
        await api(cfg.availEndpoint, 'POST', {[cfg.idKey]: assetId, day: ds, status: nextStatus});
      } catch { setAvail(p => p.map(a => a.id === ex.id ? ex : a)); }
    } else {
      const nr = {[cfg.idKey]: assetId, day: ds, status: nextStatus, id: 'tmp-'+Date.now()};
      setAvail(p => [...p, nr]);
      try {
        const sv = await api(cfg.availEndpoint, 'POST', {[cfg.idKey]: assetId, day: ds, status: nextStatus});
        setAvail(p => p.map(a => a.id === nr.id ? sv : a));
      } catch { setAvail(p => p.filter(a => a.id !== nr.id)); }
    }
  };

  return (
    <div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:12}}>
        <select className="input" style={{width:190}} value={selA}
          onChange={e => setSelA(e.target.value)}>
          <option value="">Tutti i {cfg.labelPlural}</option>
          {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {!selA && cfg.categories && cfg.categories.length > 0 && (
          <select className="input" style={{width:170}} value={catFilter}
            onChange={e => setCatFilter(e.target.value)}>
            <option value="">Tutte le categorie</option>
            {cfg.categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <div style={{display:'flex',gap:4,marginLeft:'auto'}}>
          <button className="btn btn-ghost btn-sm" onClick={prev}>
            <Icon name="chevron-left" size={15} color="var(--n500)"/>
          </button>
          <span style={{fontSize:14,color:'var(--n700)',minWidth:130,textAlign:'center',
            fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center'}}>
            {MONTHS[month]} {year}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={next}>
            <Icon name="chevron-right" size={15} color="var(--n500)"/>
          </button>
        </div>
      </div>

      <div style={{marginBottom:12,fontSize:12,color:'var(--n400)',display:'flex',gap:16,flexWrap:'wrap'}}>
        <span><span className="legend-dot" style={{background:'var(--n100)',border:'1px solid var(--n200)'}}/>Disponibile</span>
        <span><span className="legend-dot" style={{background:'var(--danger)'}}/>Non disponibile</span>
        <span><span className="legend-dot" style={{background:'#F59E0B'}}/>Manutenzione</span>
        <span><span className="legend-dot" style={{background:'var(--blue-50)',border:'1px solid var(--blue-pale)'}}/>In cantiere</span>
        <span style={{color:'var(--n300)',fontStyle:'italic'}}>Click per cambiare stato</span>
      </div>

      <div className="card" style={{padding:0,overflowX:'auto'}}>
        {loading ? <div className="empty"><Spin/></div> : (
          disp.length === 0 ? (
            <div className="empty" style={{padding:40}}>
              Nessun{isMezzi ? '' : "'"} {cfg.labelSingular} trovat{isMezzi ? 'o' : 'a'}
            </div>
          ) : (
          <table className="avail-table" style={{minWidth:200+dim*30}}>
            <thead>
              <tr>
                <th className="name-col" style={{minWidth:180}}>
                  {isMezzi ? 'Mezzo' : 'Attrezzatura'}
                </th>
                {days.map(d => (
                  <th key={d} style={{background:isWE(d)?'var(--n100)':'var(--n50)',
                    color:isWE(d)?'var(--blue)':'var(--n400)'}}>
                    <div>{d}</div>
                    <div style={{fontSize:9,opacity:.7}}>{['L','M','M','G','V','S','D'][dow(d)-1]}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {disp.map(a => (
                <tr key={a.id}>
                  <td className="name-col">{cfg.renderName(a)}</td>
                  {days.map(d => {
                    const rec = availMap[`${a.id}-${d}`];
                    const myProjs = assetProjDays[a.id]?.[d];
                    const hasPrj = !!myProjs;
                    const st = rec?.status;
                    let cls = 'avail-cell';
                    if (st === 'unavailable') cls += ' unavailable';
                    else if (st === 'maintenance') cls += ' vacation';
                    else if (hasPrj) cls += ' has-event';
                    else cls += ' available';
                    return (
                      <td key={d} style={{padding:3,background:isWE(d)?'var(--n50)':''}}>
                        <div className={cls}
                          onClick={() => { if (!hasPrj || st) toggle(a.id, d); }}
                          title={st === 'unavailable' ? `${a.name} - non disponibile`
                            : st === 'maintenance' ? `${a.name} - in manutenzione`
                            : hasPrj ? `In cantiere: ${myProjs.slice(0,2).join(', ')}`
                            : `${a.name} - disponibile`}>
                          {st === 'unavailable' && <Icon name="x" size={9} color="#fff" strokeWidth={3}/>}
                          {st === 'maintenance' && <Icon name="wrench" size={10} color="#fff" strokeWidth={2}/>}
                          {!st && hasPrj && <Icon name="clapperboard" size={9} color="var(--blue)"/>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          )
        )}
      </div>
    </div>
  );
};


export const AssetAvailPage = () => {
  const [tab, setTab] = React.useState('vehicle');

  const TabBtn = ({id, icon, label}) => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding:'8px 16px', border:'none', background:'transparent',
        borderBottom: tab === id ? '2px solid var(--blue)' : '2px solid transparent',
        color: tab === id ? 'var(--blue)' : 'var(--n500)',
        fontSize:13, fontWeight: tab === id ? 700 : 500,
        cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6,
        marginBottom:-1,
      }}>
      <Icon name={icon} size={13} color={tab === id ? 'var(--blue)' : 'var(--n400)'}/>
      {label}
    </button>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Disponibilità mezzi</div>
          <div className="page-title-sub">Calendario non disponibilità e manutenzione per mezzi e attrezzature</div>
        </div>
      </div>

      <div style={{borderBottom:'1px solid var(--n200)', marginBottom:16, display:'flex'}}>
        <TabBtn id="vehicle" icon="truck" label="Mezzi"/>
        <TabBtn id="material" icon="package" label="Attrezzature"/>
      </div>

      {tab === 'vehicle' && <AssetCalendar kind="vehicle"/>}
      {tab === 'material' && <AssetCalendar kind="material"/>}
    </div>
  );
};
